import React, { useState } from 'react';
import { HoverOver } from '../../../../../../../../GlobalComponents/Builders';

/**
 * Overloaded-destination detection and tagging, shared by mapping sections.
 *
 * A conflict group is one destination inside one parent context. Callers supply `getContext(row)` -> { key, label, leaf },
 * so composite (board/type/subtype) and value mappings (source parent) can share the same index, tags and legend.
 */

export const INACTIVE_STATUSES = ['Delete', 'Deleted', 'Ignore'];

// destination + context -> active mapped rows. `overrides` ({ [rowId]: destinationId }) simulates unsaved edits.
export const buildConflictIndex = (rows, { getContext, getId, getDestination }, overrides = {}) => {
    const index = {};
    for (const r of rows || []) {
        if (INACTIVE_STATUSES.includes(r.status)) continue;
        const id = getId(r);
        const dest = overrides[id] !== undefined ? overrides[id] : getDestination(r);
        if (!dest || Number(dest) <= 0) continue;
        const k = `${dest}|${getContext(r).key}`;
        (index[k] = index[k] || []).push(r);
    }
    return index;
};

export const conflictGroupKey = (destId, row, getContext) => `${destId}|${getContext(row).key}`;

// Other rows sharing `destId` with `row` in the same context.
export const getConflicts = (index, row, destId, { getContext, getId }) => {
    if (!destId || Number(destId) <= 0) return [];
    return (index[conflictGroupKey(destId, row, getContext)] || []).filter(r => getId(r) !== getId(row));
};

// Only groups with more than one leaf are conflicts.
export const getConflictGroups = (index) => Object.entries(index).filter(([, rows]) => rows.length > 1);

// ─── Deterministic tags ───────────────────────────────────────────────────────

// 12 hues dark enough for white text (~4.5:1+) x 2 styles (solid / outline) = 24 combinations. The code, not the color, is the identifier.
const TAG_HUES = ['#1d4ed8', '#b91c1c', '#15803d', '#c2410c', '#7e22ce', '#0f766e', '#be185d', '#92400e', '#4338ca', '#4d7c0f', '#0e7490', '#334155'];
// No I/O/0/1 so codes can't be misread.
const CODE_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const CODE_DIGITS = '23456789';

const fnv1a = (str) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
};

const codeFromHash = (h, length) => {
    let code = CODE_LETTERS[h % CODE_LETTERS.length];
    let rest = Math.floor(h / CODE_LETTERS.length);
    for (let i = 1; i < length; i++) {
        code += CODE_DIGITS[rest % CODE_DIGITS.length];
        rest = Math.floor(rest / CODE_DIGITS.length);
    }
    return code;
};

/**
 * Build { [groupKey]: { code, color, outline } } for a set of group keys.
 * Codes are 2 chars (e.g. K7) and only grow for keys that collide within the set, so the common case stays stable across reloads.
 */
export const buildTagMap = (groupKeys) => {
    const tags = {};
    const hashes = Object.fromEntries(groupKeys.map(k => [k, fnv1a(k)]));
    let pending = [...groupKeys];
    for (let length = 2; pending.length && length <= 6; length++) {
        const byCode = {};
        pending.forEach(k => { (byCode[codeFromHash(hashes[k], length)] = byCode[codeFromHash(hashes[k], length)] || []).push(k); });
        pending = [];
        Object.entries(byCode).forEach(([code, keys]) => {
            if (keys.length === 1 || length === 6) keys.forEach(k => { tags[k] = { code }; });
            else pending.push(...keys);
        });
    }
    groupKeys.forEach(k => {
        const h = fnv1a('color:' + k);
        tags[k] = { ...tags[k], color: TAG_HUES[h % TAG_HUES.length], outline: Math.floor(h / TAG_HUES.length) % 2 === 1 };
    });
    return tags;
};

// ─── Components ───────────────────────────────────────────────────────────────

export const ConflictTag = ({ tag, onClick, active = false, title }) => {
    if (!tag) return null;
    const style = {
        display: 'inline-block',
        minWidth: 34,
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'monospace',
        letterSpacing: 0.5,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        border: `2px solid ${tag.color}`,
        background: tag.outline ? '#fff' : tag.color,
        color: tag.outline ? tag.color : '#fff',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: active ? `0 0 0 2px #fff, 0 0 0 4px ${tag.color}` : 'none',
    };
    const content = <span style={style}>⚠ {tag.code}</span>;
    if (!onClick) return <span title={title}>{content}</span>;
    return (
        <button type='button' className='p-0 border-0 bg-transparent' onClick={onClick} aria-pressed={active} aria-label={title || `Overload group ${tag.code}`}>
            {content}
        </button>
    );
};

/** Tag with a hover card describing the group; click toggles the group filter. */
export const ConflictTagWithInfo = ({ tag, group, onToggle, active }) => {
    if (!tag || !group) return null;
    return (
        <HoverOver
            content={`${tag.code} — ${group.destinationLabel}\nContext: ${group.contextLabel}\nLeaves: ${group.leaves.join(', ')}\n\nClick to ${active ? 'clear the filter' : 'show only this group'}.`}
            text={<ConflictTag tag={tag} active={active} onClick={onToggle} title={`${tag.code}: ${group.destinationLabel} in ${group.contextLabel}`} />}
            contentStyle={{ maxWidth: 400 }}
            textStyle={{ minWidth: 0, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center' }}
        />
    );
};

/**
 * Collapsible legend: one row per conflict group (tag, destination, context, leaves).
 * groups: [{ key, destinationLabel, contextLabel, leaves: [] }], sorted by the caller.
 */
export const OverloadLegend = ({ groups, tags, activeKey, onSelect }) => {
    const [open, setOpen] = useState(false);
    if (!groups.length) return null;
    const destinationCount = new Set(groups.map(g => g.destinationLabel)).size;

    return (
        <div className='mb-2' style={{ border: '1px solid #ffe8a1', borderRadius: 6, background: '#fffdf5' }}>
            <button type='button' className='w-100 d-flex justify-content-between align-items-center border-0 bg-transparent px-3 py-2'
                onClick={() => setOpen(!open)} aria-expanded={open}>
                <span className='txt-warning' style={{ fontWeight: 600, fontSize: 13 }}>
                    ⚠ {groups.length} overload group{groups.length === 1 ? '' : 's'} across {destinationCount} destination{destinationCount === 1 ? '' : 's'}
                </span>
                <span className='text-muted' style={{ fontSize: 12 }}>{open ? 'Hide legend ▲' : 'Show legend ▼'}</span>
            </button>
            {open && (
                <div className='px-3 pb-2'>
                    <small className='text-muted d-block mb-2'>Each tag is one destination overloaded inside one board/type context: reverse sync can't tell its leaves apart. Click a tag to filter the table.</small>
                    <table className='table table-sm mb-0' style={{ fontSize: 13 }}>
                        <thead>
                            <tr><th scope='col'>Tag</th><th scope='col'>Destination</th><th scope='col'>Context</th><th scope='col'>Leaves</th></tr>
                        </thead>
                        <tbody>
                            {groups.map(g => (
                                <tr key={g.key} style={activeKey === g.key ? { background: '#fff3cd' } : undefined}>
                                    <td><ConflictTag tag={tags[g.key]} active={activeKey === g.key} onClick={() => onSelect(activeKey === g.key ? null : g.key)} title={`Filter to ${tags[g.key]?.code}`} /></td>
                                    <td>{g.destinationLabel}</td>
                                    <td>{g.contextLabel}</td>
                                    <td>{g.leaves.join(', ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
